/** Server list sort: `ordering=field` (asc) or `ordering=-field` (desc). */

export type SortDirection = 'asc' | 'desc';

export type TableSortState<SortKey extends string> = {
  key: SortKey | null;
  direction: SortDirection;
};

export function toggleSortState<SortKey extends string>(
  current: TableSortState<SortKey>,
  key: SortKey,
): TableSortState<SortKey> {
  return {
    key,
    direction:
      current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  };
}

/** Map API ordering param back to UI sort state (best-effort; unknown fields ignored). */
export function parseOrderingParam<SortKey extends string>(
  raw: string | null | undefined,
  fieldToSortKey: Record<string, SortKey>,
): TableSortState<SortKey> | null {
  if (raw == null || !String(raw).trim()) return null;
  const s = String(raw).trim();
  const desc = s.startsWith('-');
  const field = desc ? s.slice(1) : s;
  const key = fieldToSortKey[field];
  if (!key) return null;
  return { key, direction: desc ? 'desc' : 'asc' };
}

/** Build `ordering` query value from UI state; omit unsupported columns. */
export function toOrderingParam<SortKey extends string>(
  state: TableSortState<SortKey>,
  sortKeyToField: Record<SortKey, string | undefined>,
): string | null {
  if (!state.key) return null;
  const field = sortKeyToField[state.key];
  if (!field) return null;
  return state.direction === 'desc' ? `-${field}` : field;
}

export function parseOrderingErrorBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const detail = o.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const allowed = o.allowed_ordering;
  if (Array.isArray(allowed) && allowed.length > 0) {
    return `This list cannot sort by that column. Allowed: ${allowed.join(', ')}`;
  }
  return null;
}
